import React, { FC, ReactNode } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { imageAmplienceToUrl } from 'helpers/utils/amplience/imageAmplienceToUrl';
import { amplienceImageLoader } from 'helpers/utils/amplience/imageLoader';
import { getPageType } from 'helpers/utils/cjTagDataHelpers';
import { classnames, cn } from 'helpers/utils/classnames';
import { gtmClickCta } from 'helpers/utils/gtm-events/gtmInteraction';
import { layout } from 'static/constants';
import { AmpPromotionEvent } from '../../../../types/gtm-events/Ecommerce';
import GTMWrapper from '../gtm-wrapper';
import { AmplienceImage } from '../types';
export interface AmplienceContentCardProps {
  title: string;
  subtitle?: string;
  cta?: string;
  ctaLink?: string;
  imageAltText: string;
  image: AmplienceImage;
  gtmEvents: AmpPromotionEvent[];
  height?: number;
}

const AmplienceContentCard: FC<AmplienceContentCardProps & { numberOfColumns?: number }> = ({
  image,
  imageAltText,
  title,
  cta,
  ctaLink,
  gtmEvents,
  subtitle,
  numberOfColumns = 1,
  height,
}) => {
  const Wrapper = ctaLink ? LinkWrapper : ContainerWrapper;

  const path = usePathname();

  const pageType = getPageType({
    locale: 'en',
    path: path,
  });

  const onClickCta = () => {
    gtmClickCta({ name: cta ?? '', location: pageType });
  };

  return (
    <GTMWrapper gtmEvents={gtmEvents}>
      <Wrapper link={ctaLink || ''}>
        <div
          className={cn('relative w-full justify-center', !height && 'aspect-[1.5]')}
          style={height ? { height: `${height}px` } : undefined}
        >
          <Image
            fill
            loader={amplienceImageLoader}
            src={imageAmplienceToUrl(image)}
            sizes={`(max-width: 1023px) 100vw, ${Math.floor(layout.pageMaxWidth / numberOfColumns)}px`}
            alt={imageAltText}
            className="rounded-t-[6px] object-cover"
          />
        </div>
        <div className="flex w-full items-center justify-between rounded-b-[6px] bg-card-background p-[16px]">
          <div className={classnames({ 'w-3/5': cta !== undefined, 'w-full text-center': !cta })}>
            <h3 className="mb-[2px] text-sm font-semibold leading-[17px] lg:text-xl lg:leading-[24px]">{title}</h3>
            <p className="text-xs font-normal leading-[14px] lg:text-sm lg:leading-[17px]">{subtitle}</p>
          </div>
          {cta && (
            <div className="flex w-2/5 justify-end">
              <div
                role="button"
                onClick={onClickCta}
                className="w-fit rounded bg-card-button px-[24px] py-[8px] text-xs font-medium text-white hover:bg-gray-600 lg:text-sm"
              >
                {cta}
              </div>
            </div>
          )}
        </div>
      </Wrapper>
    </GTMWrapper>
  );
};

const LinkWrapper = ({ children, link, title }: { children: ReactNode; link: string; title?: string }) => {
  return (
    <Link aria-label={title} onClick={(e) => e.stopPropagation()} href={link}>
      {children}
    </Link>
  );
};
const ContainerWrapper = ({ children }: { children: ReactNode }) => {
  return <div>{children}</div>;
};

export default AmplienceContentCard;
